import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Clock, Calendar, Plus, Edit2, Trash2, Save, AlertCircle, CheckCircle, ExternalLink, RefreshCw, CalendarDays, Settings } from 'lucide-react';
import axios from 'axios';

const ConfigureHours = () => {
  const { user, token, isAdmin, canAccessManager, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [hospitalHours, setHospitalHours] = useState({
    monday: { is_open: true, open_time: '09:00', close_time: '18:00' },
    tuesday: { is_open: true, open_time: '09:00', close_time: '18:00' },
    wednesday: { is_open: true, open_time: '09:00', close_time: '18:00' },
    thursday: { is_open: true, open_time: '09:00', close_time: '18:00' },
    friday: { is_open: true, open_time: '09:00', close_time: '18:00' },
    saturday: { is_open: true, open_time: '09:00', close_time: '17:00' },
    sunday: { is_open: false, open_time: '', close_time: '' }
  });

  const [urgentCareHours, setUrgentCareHours] = useState({
    monday: { is_open: true, open_time: '15:00', close_time: '22:00' },
    tuesday: { is_open: true, open_time: '15:00', close_time: '22:00' },
    wednesday: { is_open: true, open_time: '15:00', close_time: '22:00' },
    thursday: { is_open: true, open_time: '15:00', close_time: '22:00' },
    friday: { is_open: true, open_time: '15:00', close_time: '22:00' },
    saturday: { is_open: true, open_time: '15:00', close_time: '22:00' },
    sunday: { is_open: true, open_time: '15:00', close_time: '22:00' }
  });

  const [specialHours, setSpecialHours] = useState([]);
  const [newSpecialHour, setNewSpecialHour] = useState({
    date: '',
    name: '',
    general_practice: { is_open: false, open_time: '', close_time: '' },
    urgent_care: { is_open: false, open_time: '', close_time: '' }
  });

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [authError, setAuthError] = useState(null); // New state for authentication errors
  const [pageLoading, setPageLoading] = useState(true); // New state for page loading

  // Business model settings
  const [multipleBusinessesEnabled, setMultipleBusinessesEnabled] = useState(false);
  const [onlineOnlyEnabled, setOnlineOnlyEnabled] = useState(false);
  const [businessHoursLabel, setBusinessHoursLabel] = useState('Business Hours');
  const [secondBusinessHoursLabel, setSecondBusinessHoursLabel] = useState('2nd Business Hours');
  const [editingBusinessLabel, setEditingBusinessLabel] = useState(false);
  const [editingSecondBusinessLabel, setEditingSecondBusinessLabel] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Dynamic tabs based on settings
  const getTabs = () => {
    if (onlineOnlyEnabled) {
      return []; // No tabs when online only
    }
    
    const tabsList = [
      { 
        id: 'general', 
        label: businessHoursLabel, 
        icon: Clock,
        color: '#374151'
      }
    ];
    
    if (multipleBusinessesEnabled) {
      tabsList.push({ 
        id: 'urgent', 
        label: secondBusinessHoursLabel, 
        icon: Clock,
        color: '#374151'
      });
    }
    
    tabsList.push({ 
      id: 'special', 
      label: 'Special Holidays', 
      icon: CalendarDays,
      color: '#7c3aed'
    });
    
    return tabsList;
  };

  // Authentication useEffect - same pattern as PhotoManagement
  useEffect(() => {
    // Wait for auth to finish loading before checking permissions
    if (authLoading) {
      return;
    }

    // If there's a token but no user yet, wait for user profile to load
    if (token && !user) {
      return;
    }

    if (user && canAccessManager()) {
      setAuthError(null);
      setPageLoading(false);
    } else if (user && !canAccessManager()) {
      setAuthError('Access denied. Manager or admin privileges required.');
      setPageLoading(false);
    } else if (!token) {
      // Only show error if there's definitely no token
      setAuthError('You need manager or administrator privileges to configure hospital hours.');
      setPageLoading(false);
    }
  }, [user, token, authLoading]);

  // Existing useEffects start here...

  useEffect(() => {
    // Only fetch data if user is authenticated and authorized
    if (!pageLoading && user && canAccessManager()) {
      fetchHours();
      checkGoogleConnection();
    }
  }, [pageLoading, user, canAccessManager]);

  const checkGoogleConnection = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/google-business/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setGoogleConnected(response.data.is_connected || false);
    } catch (err) {
      console.error('Error checking Google connection:', err);
    }
  };

  const handleGoogleSync = async (syncType) => {
    setSyncing(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/google-business/sync`, 
        { sync_type: syncType },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: `${syncType === 'general_practice' ? 'General Practice' : 'Urgent Care'} hours synced to Google Business Profile successfully!` });
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Sync failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to sync to Google Business Profile' });
    } finally {
      setSyncing(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const fetchHours = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch hospital hours
      const hospitalResponse = await fetch(`${API_BASE_URL}/api/hospital-hours`, { headers });
      if (hospitalResponse.ok) {
        const hospitalData = await hospitalResponse.json();
        setHospitalHours({
          monday: hospitalData.monday,
          tuesday: hospitalData.tuesday,
          wednesday: hospitalData.wednesday,
          thursday: hospitalData.thursday,
          friday: hospitalData.friday,
          saturday: hospitalData.saturday,
          sunday: hospitalData.sunday
        });
      }

      // Fetch urgent care hours
      const urgentResponse = await fetch(`${API_BASE_URL}/api/urgent-care-hours`, { headers });
      if (urgentResponse.ok) {
        const urgentData = await urgentResponse.json();
        setUrgentCareHours({
          monday: urgentData.monday,
          tuesday: urgentData.tuesday,
          wednesday: urgentData.wednesday,
          thursday: urgentData.thursday,
          friday: urgentData.friday,
          saturday: urgentData.saturday,
          sunday: urgentData.sunday
        });
      }

      // Fetch special hours
      const specialResponse = await fetch(`${API_BASE_URL}/api/special-hours`, { headers });
      if (specialResponse.ok) {
        const specialData = await specialResponse.json();
        setSpecialHours(specialData);
      }

      // Fetch business model settings
      await fetchBusinessModelSettings();

    } catch (error) {
      console.error('Error fetching hours:', error);
      setMessage({ type: 'error', text: 'Failed to load hours data.' });
    }
  };

  const fetchBusinessModelSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-model-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setMultipleBusinessesEnabled(response.data.multiple_businesses_enabled || false);
        setOnlineOnlyEnabled(response.data.online_only || false);
        setBusinessHoursLabel(response.data.business_hours_label || 'Business Hours');
        setSecondBusinessHoursLabel(response.data.second_business_hours_label || '2nd Business Hours');
      }
    } catch (error) {
      console.error('Error fetching business model settings:', error);
    }
  };

  const saveBusinessModelSettings = async (overrides = {}) => {
    try {
      const dataToSave = {
        multiple_businesses_enabled: overrides.multiple_businesses_enabled !== undefined 
          ? overrides.multiple_businesses_enabled 
          : multipleBusinessesEnabled,
        online_only: overrides.online_only !== undefined 
          ? overrides.online_only 
          : onlineOnlyEnabled,
        business_hours_label: overrides.business_hours_label || businessHoursLabel,
        second_business_hours_label: overrides.second_business_hours_label || secondBusinessHoursLabel
      };
      
      const response = await axios.put(
        `${API_BASE_URL}/api/business-model-settings`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Business model settings saved successfully' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Error saving business model settings:', error);
      setMessage({ type: 'error', text: 'Failed to save business model settings' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleDayChange = (type, day, field, value) => {
    const setter = type === 'hospital' ? setHospitalHours : setUrgentCareHours;
    const current = type === 'hospital' ? hospitalHours : urgentCareHours;
    
    setter({
      ...current,
      [day]: {
        ...current[day],
        [field]: value
      }
    });
  };

  const updateHospitalHours = (day, field, value) => {
    setHospitalHours({
      ...hospitalHours,
      [day]: {
        ...hospitalHours[day],
        [field]: value
      }
    });
  };

  const updateUrgentCareHours = (day, field, value) => {
    setUrgentCareHours({
      ...urgentCareHours,
      [day]: {
        ...urgentCareHours[day],
        [field]: value
      }
    });
  };

  const saveHours = async (type) => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const endpoint = type === 'hospital' ? 'hospital-hours' : 'urgent-care-hours';
      const data = type === 'hospital' ? hospitalHours : urgentCareHours;

      const response = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: `${type === 'hospital' ? 'Hospital' : 'Urgent Care'} hours updated successfully!` 
        });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to update hours' });
      }
    } catch (error) {
      console.error('Error saving hours:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };


  const addSpecialHour = async () => {
    if (!newSpecialHour.date || !newSpecialHour.name) {
      setMessage({ type: 'error', text: 'Please fill in date and name for special hours.' });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/special-hours`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSpecialHour)
      });

      if (response.ok) {
        const newHour = await response.json();
        setSpecialHours([...specialHours, newHour]);
        setNewSpecialHour({
          date: '',
          name: '',
          general_practice: { is_open: false, open_time: '', close_time: '' },
          urgent_care: { is_open: false, open_time: '', close_time: '' }
        });
        setMessage({ type: 'success', text: 'Special hours added successfully!' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to add special hours' });
      }
    } catch (error) {
      console.error('Error adding special hours:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  const deleteSpecialHour = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/special-hours/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        setSpecialHours(specialHours.filter(hour => hour.id !== id));
        setMessage({ type: 'success', text: 'Special hours deleted successfully!' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to delete special hours' });
      }
    } catch (error) {
      console.error('Error deleting special hours:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Show loading while authentication is being determined
  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto mb-3"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading</h3>
            <p className="text-sm text-gray-600">
              {authLoading ? 'Verifying your access permissions...' : 'Loading your profile...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{authError}</p>
            <p className="text-red-600 text-sm mt-2">Please log in with a manager or admin account.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Compact Design like Business Info */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Configure Hours
                  </h2>
                </div>
                
                {/* Toggle Buttons */}
                <div className="flex flex-wrap items-center gap-4">
                  {/* Online Only Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Online Only</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onlineOnlyEnabled}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setOnlineOnlyEnabled(newValue);
                          if (newValue) {
                            setMultipleBusinessesEnabled(false);
                            saveBusinessModelSettings({ 
                              online_only: newValue, 
                              multiple_businesses_enabled: false 
                            });
                          } else {
                            saveBusinessModelSettings({ online_only: newValue });
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={onlineOnlyEnabled ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>

                  {/* Multiple Businesses Toggle */}
                  {!onlineOnlyEnabled && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Multiple Businesses</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={multipleBusinessesEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setMultipleBusinessesEnabled(newValue);
                            if (!newValue && activeTab === 'urgent') {
                              setActiveTab('general');
                            }
                            saveBusinessModelSettings({ multiple_businesses_enabled: newValue });
                          }}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={multipleBusinessesEnabled ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            {!onlineOnlyEnabled && getTabs().length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                {getTabs().map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                        isActive
                          ? 'border-theme-blue text-theme-blue'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                      
                      {/* Edit icon for business hours tabs */}
                      {(tab.id === 'general' || tab.id === 'urgent') && isActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tab.id === 'general') {
                              setEditingBusinessLabel(true);
                            } else {
                              setEditingSecondBusinessLabel(true);
                            }
                          }}
                          className="ml-1 p-1 hover:bg-gray-100 rounded"
                        >
                          <Edit2 className="h-3 w-3" />
                        </button>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-3 sm:p-6">
            {/* General Practice Hours Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {days.map(day => (
                    <div key={day.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">{day.label}</h3>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hospitalHours[day.key]?.is_open || false}
                            onChange={(e) => updateHospitalHours(day.key, 'is_open', e.target.checked)}
                            className="sr-only"
                          />
                          <div className="relative inline-flex items-center h-6 rounded-full w-11 transition-colors"
                            style={{
                              backgroundColor: hospitalHours[day.key]?.is_open ? 'var(--theme-primary)' : '#e5e7eb'
                            }}>
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                              hospitalHours[day.key]?.is_open ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                          <span className="ml-2 text-sm text-gray-600">
                            {hospitalHours[day.key]?.is_open ? 'Open' : 'Closed'}
                          </span>
                        </label>
                      </div>
                      
                      {hospitalHours[day.key]?.is_open && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Open Time</label>
                            <input
                              type="time"
                              value={hospitalHours[day.key]?.open_time || ''}
                              onChange={(e) => updateHospitalHours(day.key, 'open_time', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Close Time</label>
                            <input
                              type="time"
                              value={hospitalHours[day.key]?.close_time || ''}
                              onChange={(e) => updateHospitalHours(day.key, 'close_time', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Action Buttons at Bottom */}
                <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pt-6 border-t border-gray-200 mt-8">
                  {/* Google Sync Button */}
                  {googleConnected ? (
                    <button
                      onClick={() => handleGoogleSync('general_practice')}
                      disabled={syncing || loading}
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                      style={{ 
                        backgroundColor: syncing ? '#94a3b8' : '#10b981'
                      }}
                      onMouseEnter={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#10b981';
                      }}
                    >
                      {syncing ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Sync to Google
                        </>
                      )}
                    </button>
                  ) : (
                    <a
                      href="/google-integration"
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors no-underline"
                      style={{ backgroundColor: '#10b981' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Connect Google
                    </a>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={() => saveHours('hospital')}
                    disabled={loading}
                    className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                    style={{ 
                      backgroundColor: loading ? '#94a3b8' : 'var(--theme-primary)'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) e.target.style.backgroundColor = 'var(--theme-primary)';
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Urgent Care Hours Tab */}
            {activeTab === 'urgent' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {days.map(day => (
                    <div key={day.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">{day.label}</h3>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={urgentCareHours[day.key]?.is_open || false}
                            onChange={(e) => updateUrgentCareHours(day.key, 'is_open', e.target.checked)}
                            className="sr-only"
                          />
                          <div className="relative inline-flex items-center h-6 rounded-full w-11 transition-colors"
                            style={{
                              backgroundColor: urgentCareHours[day.key]?.is_open ? 'var(--theme-primary)' : '#e5e7eb'
                            }}>
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                              urgentCareHours[day.key]?.is_open ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                          <span className="ml-2 text-sm text-gray-600">
                            {urgentCareHours[day.key]?.is_open ? 'Open' : 'Closed'}
                          </span>
                        </label>
                      </div>
                      
                      {urgentCareHours[day.key]?.is_open && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Open Time</label>
                            <input
                              type="time"
                              value={urgentCareHours[day.key]?.open_time || ''}
                              onChange={(e) => updateUrgentCareHours(day.key, 'open_time', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Close Time</label>
                            <input
                              type="time"
                              value={urgentCareHours[day.key]?.close_time || ''}
                              onChange={(e) => updateUrgentCareHours(day.key, 'close_time', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Action Buttons at Bottom */}
                <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pt-6 border-t border-gray-200 mt-8">
                  {/* Google Sync Button */}
                  {googleConnected ? (
                    <button
                      onClick={() => handleGoogleSync('urgent_care')}
                      disabled={syncing || loading}
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                      style={{ 
                        backgroundColor: syncing ? '#94a3b8' : '#10b981'
                      }}
                      onMouseEnter={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#10b981';
                      }}
                    >
                      {syncing ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Sync to Google
                        </>
                      )}
                    </button>
                  ) : (
                    <a
                      href="/google-integration"
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors no-underline"
                      style={{ backgroundColor: '#10b981' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Connect Google
                    </a>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={() => saveHours('urgent')}
                    disabled={loading}
                    className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                    style={{ 
                      backgroundColor: loading ? '#94a3b8' : 'var(--theme-primary)'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) e.target.style.backgroundColor = 'var(--theme-primary)';
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}


            {/* Special Holidays Tab */}
            {activeTab === 'special' && (
              <div className="space-y-6">
                {/* Add New Special Hour Form */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Add Special Holiday Hours</h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={newSpecialHour.date}
                        onChange={(e) => setNewSpecialHour(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ "--tw-ring-color": "var(--theme-primary)" }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Holiday Name</label>
                      <input
                        type="text"
                        value={newSpecialHour.name}
                        onChange={(e) => setNewSpecialHour(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ "--tw-ring-color": "var(--theme-primary)" }}
                        placeholder="e.g., Christmas Day, Thanksgiving"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    {/* General Practice Special Hours */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">General Practice</h4>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newSpecialHour.general_practice.is_open}
                            onChange={(e) => setNewSpecialHour(prev => ({
                              ...prev,
                              general_practice: { ...prev.general_practice, is_open: e.target.checked }
                            }))}
                            className="sr-only"
                          />
                          <div className="relative inline-flex items-center h-6 rounded-full w-11 transition-colors"
                            style={{
                              backgroundColor: newSpecialHour.general_practice.is_open ? 'var(--theme-primary)' : '#e5e7eb'
                            }}>
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                              newSpecialHour.general_practice.is_open ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                          <span className="ml-2 text-sm text-gray-600">
                            {newSpecialHour.general_practice.is_open ? 'Open' : 'Closed'}
                          </span>
                        </label>
                      </div>
                      
                      {newSpecialHour.general_practice.is_open && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Open</label>
                            <input
                              type="time"
                              value={newSpecialHour.general_practice.open_time}
                              onChange={(e) => setNewSpecialHour(prev => ({
                                ...prev,
                                general_practice: { ...prev.general_practice, open_time: e.target.value }
                              }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Close</label>
                            <input
                              type="time"
                              value={newSpecialHour.general_practice.close_time}
                              onChange={(e) => setNewSpecialHour(prev => ({
                                ...prev,
                                general_practice: { ...prev.general_practice, close_time: e.target.value }
                              }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Urgent Care Special Hours */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Urgent Care</h4>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newSpecialHour.urgent_care.is_open}
                            onChange={(e) => setNewSpecialHour(prev => ({
                              ...prev,
                              urgent_care: { ...prev.urgent_care, is_open: e.target.checked }
                            }))}
                            className="sr-only"
                          />
                          <div className="relative inline-flex items-center h-6 rounded-full w-11 transition-colors"
                            style={{
                              backgroundColor: newSpecialHour.urgent_care.is_open ? 'var(--theme-primary)' : '#e5e7eb'
                            }}>
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                              newSpecialHour.urgent_care.is_open ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                          <span className="ml-2 text-sm text-gray-600">
                            {newSpecialHour.urgent_care.is_open ? 'Open' : 'Closed'}
                          </span>
                        </label>
                      </div>
                      
                      {newSpecialHour.urgent_care.is_open && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Open</label>
                            <input
                              type="time"
                              value={newSpecialHour.urgent_care.open_time}
                              onChange={(e) => setNewSpecialHour(prev => ({
                                ...prev,
                                urgent_care: { ...prev.urgent_care, open_time: e.target.value }
                              }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Close</label>
                            <input
                              type="time"
                              value={newSpecialHour.urgent_care.close_time}
                              onChange={(e) => setNewSpecialHour(prev => ({
                                ...prev,
                                urgent_care: { ...prev.urgent_care, close_time: e.target.value }
                              }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1"
                              style={{ "--tw-ring-color": "var(--theme-primary)" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-stretch sm:justify-end">
                    <button
                      onClick={addSpecialHour}
                      disabled={loading || !newSpecialHour.date || !newSpecialHour.name}
                      className="w-full sm:w-auto text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start font-medium"
                      style={{ 
                        backgroundColor: (loading || !newSpecialHour.date || !newSpecialHour.name) ? '#94a3b8' : 'var(--theme-primary)'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading && newSpecialHour.date && newSpecialHour.name) e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                      }}
                      onMouseLeave={(e) => {
                        if (!loading && newSpecialHour.date && newSpecialHour.name) e.target.style.backgroundColor = 'var(--theme-primary)';
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Special Hours
                    </button>
                  </div>
                </div>

                {/* Existing Special Hours List */}
                {specialHours.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Existing Special Hours</h3>
                    <div className="space-y-4">
                      {specialHours.map((specialHour) => (
                        <div key={specialHour.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-gray-900">{specialHour.name}</h4>
                              <p className="text-sm text-gray-600">{new Date(specialHour.date).toLocaleDateString()}</p>
                            </div>
                            <button
                              onClick={() => deleteSpecialHour(specialHour.id)}
                              className="text-red-600 hover:text-red-700 p-1"
                              title="Delete Special Hours"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-theme-blue">General Practice:</span>
                              <span className="ml-2">
                                {specialHour.general_practice.is_open 
                                  ? `${specialHour.general_practice.open_time} - ${specialHour.general_practice.close_time}`
                                  : 'Closed'
                                }
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-red-600">Urgent Care:</span>
                              <span className="ml-2">
                                {specialHour.urgent_care.is_open 
                                  ? `${specialHour.urgent_care.open_time} - ${specialHour.urgent_care.close_time}`
                                  : 'Closed'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {specialHours.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <CalendarDays className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No special hours set yet</h3>
                    <p className="text-gray-600">Add special holiday hours using the form above</p>
                  </div>
                )}
                
                {/* Action Buttons at Bottom */}
                <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pt-6 border-t border-gray-200 mt-8">
                  {/* Google Sync Button */}
                  {googleConnected ? (
                    <button
                      onClick={() => handleGoogleSync('special_hours')}
                      disabled={syncing || loading}
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                      style={{ 
                        backgroundColor: syncing ? '#94a3b8' : '#10b981'
                      }}
                      onMouseEnter={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#10b981';
                      }}
                    >
                      {syncing ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Sync to Google
                        </>
                      )}
                    </button>
                  ) : (
                    <a
                      href="/google-integration"
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors no-underline"
                      style={{ backgroundColor: '#10b981' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Connect Google
                    </a>
                  )}
                </div>
              </div>
            )}
            </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Business Label Modal */}
      {editingBusinessLabel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Business Hours Label</h3>
            <input
              type="text"
              value={businessHoursLabel}
              onChange={(e) => setBusinessHoursLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
              placeholder="e.g., Business Hours, General Practice Hours"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditingBusinessLabel(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  saveBusinessModelSettings({ business_hours_label: businessHoursLabel });
                  setEditingBusinessLabel(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Second Business Label Modal */}
      {editingSecondBusinessLabel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Second Business Hours Label</h3>
            <input
              type="text"
              value={secondBusinessHoursLabel}
              onChange={(e) => setSecondBusinessHoursLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
              placeholder="e.g., 2nd Business Hours, Urgent Care Hours"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditingSecondBusinessLabel(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  saveBusinessModelSettings({ second_business_hours_label: secondBusinessHoursLabel });
                  setEditingSecondBusinessLabel(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Online Only Message */}
      {onlineOnlyEnabled && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Online Only Business</h3>
            <p className="text-sm text-blue-700">
              Business hours configuration is disabled for online-only businesses. 
              Toggle off &quot;Online Only&quot; to configure business hours and special holidays.
            </p>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ConfigureHours;